import { Duration } from "luxon";

import type { WsAppSyncEvent } from "@storyboard/web-client";
import { createLogger } from "@/logger";
import { mergeEvents, queues } from "@/models/appSyncFunctions.ts";
import { flightRecorder } from "@/services/debug/FlightRecorder";

const log = createLogger("appsync");

export const MIN_QUEUE_INTERVAL_MS = Duration.fromObject({ seconds: 1 }).toMillis();
export const MAX_QUEUE_INTERVAL_MS = Duration.fromObject({ seconds: 10 }).toMillis();

/** Attempts at the merged batch before it is split and applied event by event. */
const MERGED_BATCH_ATTEMPTS = 2;

type QueueEventProcessor = (event: WsAppSyncEvent, accountId: string, workspaceId: string) => Promise<void>;

/**
 * Re-fetches the feeds named by events the drain loop discarded.
 *
 * Registered by the ingest surface rather than called directly so this module keeps no dependency on the bootstrap
 * or client layers, matching how `recoverFromBootstrapDivergence` takes its `rebootstrap`.
 */
export type DiscardedFeedRecovery = (feedIds: string[], accountId: string, workspaceId: string) => Promise<void>;

let processor: QueueEventProcessor | undefined;
let recoverFeeds: DiscardedFeedRecovery | undefined;
let isProcessing = false;
let queueTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Single pending-drain timer: replacing it on every schedule keeps exactly one loop alive.
 *
 * This stays hand-rolled rather than moving to Pacer's `AsyncQueuer` (WEB-2218). The queuer paces
 * *items you add to it* and stops when it runs dry, where this is a self-rescheduling poll over the
 * externally-owned `queues` map whose next delay is derived from how long the last pass took. Making
 * it a queuer would mean pushing a synthetic item per tick purely to keep the loop alive.
 */
function scheduleNextQueueProcess(delayMs: number): void {
  if (queueTimer) clearTimeout(queueTimer);
  queueTimer = setTimeout(
    () => {
      queueTimer = null;
      void processQueues();
    },
    Math.min(Math.max(delayMs, MIN_QUEUE_INTERVAL_MS), MAX_QUEUE_INTERVAL_MS),
  );
}

/**
 * Feed ids named by `events`, over a superset of the tables `accumulateBootstrapDelta` scopes a warm boot to — the
 * same set plus `feedPreferences`.
 *
 * `feeds` contributes its row ids; every other table contributes its `feedId`. Tables that name no feed are ignored —
 * an account or workspace row carries nothing a feed-scoped re-fetch could act on, so a batch of only those recovers
 * to an empty set and skips the re-fetch entirely.
 */
function feedIdsInEvents(events: readonly WsAppSyncEvent[]): string[] {
  const feedIds = new Set<string>();
  for (const event of events) {
    for (const feed of event.feeds ?? []) {
      if (feed.id) feedIds.add(feed.id);
    }
    for (const row of [
      ...(event.items ?? []),
      ...(event.events ?? []),
      ...(event.permissions ?? []),
      ...(event.commandAliases ?? []),
      ...(event.feedPreferences ?? []),
    ]) {
      if (row.feedId) feedIds.add(row.feedId);
    }
  }
  return Array.from(feedIds);
}

/**
 * Applies one queue's events, retrying the merged batch before falling back to applying them individually.
 *
 * A retry covers the transient half of the failure space — a racing store write, a dependency that was momentarily
 * absent — and re-applying is safe because every ingest path upserts by id. A second failure means the batch is
 * probably poison, so it is split: applying each event on its own narrows the loss from everything queued for the
 * account+workspace to only the events that actually throw. Splitting is what makes the drop proportionate;
 * `mergeEvents` is otherwise wide enough that one bad row costs an entire drain's worth of rows.
 *
 * @returns the events that still threw once applied individually. These are dropped — re-queueing a poison batch
 *   would retry it forever — and their feeds are handed to {@link DiscardedFeedRecovery} to heal.
 */
async function applyQueuedEvents(
  events: WsAppSyncEvent[],
  accountId: string,
  workspaceId: string,
): Promise<WsAppSyncEvent[]> {
  const apply = processor;
  if (!apply) return events;

  const mergedEvent = mergeEvents(events);
  for (let attempt = 1; attempt <= MERGED_BATCH_ATTEMPTS; attempt++) {
    try {
      flightRecorder.note("appsync");
      await apply(mergedEvent, accountId, workspaceId);
      return [];
    } catch (error) {
      // Warn rather than error: a first failure is often transient and the retry or the split below recovers it, so
      // one occurrence is not worth paging anyone. The terminal drop below is what carries the signal.
      log.warn("[appsyncQueueProcessor] Merged batch application failed", { accountId, workspaceId, attempt, error });
    }
  }

  const discarded: WsAppSyncEvent[] = [];
  for (const event of events) {
    try {
      await apply(event, accountId, workspaceId);
    } catch (error) {
      discarded.push(event);
      log.warn("[appsyncQueueProcessor] Discarded a single event after the split retry threw", {
        accountId,
        workspaceId,
        error,
      });
    }
  }
  return discarded;
}

/**
 * Reports the discarded events and re-fetches the feeds they named.
 *
 * Dropping stays deliberate; the re-fetch is what stops a drop from leaving the tab quietly missing rows for the rest
 * of its life. Bootstrap cursors advance independently of AppSync application, so nothing else revisits this window:
 * the next incremental bootstrap starts after the lost events, and the divergence probe only fires on empty
 * collections, which a partial loss never produces.
 */
async function recoverDiscardedEvents(
  discarded: WsAppSyncEvent[],
  accountId: string,
  workspaceId: string,
): Promise<void> {
  const feedIds = feedIdsInEvents(discarded);

  // TODO - WEB-2240: replace this `log.error` with a `MetricEmitter` counter (dimensions: workspace + event kinds).
  // The drop rate is an aggregate signal — per the error-reporting skill the right response to one occurrence is
  // "watch the dashboard", so it belongs in CloudWatch/Grafana rather than Sentry. It stays an `error` in the
  // meantime only because `packages/web` has no path to a CloudWatch metric yet, and downgrading it before that
  // path exists would drop today's per-occurrence signal without putting the rate anywhere.
  log.error("[appsyncQueueProcessor] Dropped events after their processor threw", {
    accountId,
    workspaceId,
    discardedEvents: discarded.length,
    affectedFeeds: feedIds.length,
  });

  if (!recoverFeeds || feedIds.length === 0) return;
  await recoverFeeds(feedIds, accountId, workspaceId);
}

/**
 * Drains every account/workspace queue through the registered processor, merging each queue's
 * accumulated events into one batch. Applying a batch is a synchronous set of in-memory store
 * upserts, so drains never contend with rendering or boot the way the old single-connection
 * PGlite writes did — there is no boot-gate pause anymore.
 */
export async function processQueues(): Promise<void> {
  if (isProcessing || !processor) return;

  isProcessing = true;
  const start = performance.now();

  try {
    for (const queueData of queues.values()) {
      if (queueData.events.length === 0) continue;

      const eventsToProcess = queueData.events;
      queueData.events = [];

      try {
        const discarded = await applyQueuedEvents(eventsToProcess, queueData.accountId, queueData.workspaceId);
        if (discarded.length > 0) {
          await recoverDiscardedEvents(discarded, queueData.accountId, queueData.workspaceId);
        }
      } catch (error) {
        // Nothing above is allowed to abort the pass: a queue whose recovery also fails must not stop the remaining
        // queues from draining.
        log.error("[appsyncQueueProcessor] Failed to drain a queue", {
          accountId: queueData.accountId,
          workspaceId: queueData.workspaceId,
          error,
        });
      }
    }
  } finally {
    isProcessing = false;
    const elapsed = performance.now() - start;
    scheduleNextQueueProcess(elapsed * 2);
  }
}

/**
 * Registers the merged-event processor and starts the drain loop.
 *
 * @param recoverDiscardedFeeds re-fetch for the feeds behind dropped events. Optional so the drop path never depends
 *   on it being wired: without it a drop still logs, it just no longer heals.
 */
export function startQueueProcessing(
  handler: QueueEventProcessor,
  recoverDiscardedFeeds?: DiscardedFeedRecovery,
): void {
  processor = handler;
  recoverFeeds = recoverDiscardedFeeds;
  scheduleNextQueueProcess(MIN_QUEUE_INTERVAL_MS);
}

/** Test-only reset of the loop's module state; production code never tears the loop down. */
export function __resetQueueProcessingForTests(): void {
  processor = undefined;
  recoverFeeds = undefined;
  isProcessing = false;
  if (queueTimer) {
    clearTimeout(queueTimer);
    queueTimer = null;
  }
}
